import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query-v5';
import api from '../api';
import {
  SpendAreaResourceDto,
  SpendCategoryListDto,
  RequestSpendAreaResponseDto,
} from '../api-client';
import { MAXIMUM_PAGINATION } from '../constants';
import { QueryKey } from '../enums';

export const useSpendAreas = (
  organizationId: string,
  options?: UseQueryOptions<SpendAreaResourceDto, Error, SpendAreaResourceDto>
) => {
  return useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.SpendArea,
      QueryKey.Operation.List,
      organizationId,
    ],
    queryFn: async () => {
      return api.organization.organizationControllerGetOrganizationSpendCategory(
        organizationId
      );
    },
  });
};

export const useSpendCategoryNames = (
  organizationId: string,
  query?: Parameters<
    typeof api.organization.organizationControllerGetAllSpendCategories
  >[1],
  options?: UseQueryOptions<SpendCategoryListDto, Error, SpendCategoryListDto>
) => {
  return useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.SpendCategoryNames,
      QueryKey.Operation.List,
      query,
      organizationId,
    ],
    queryFn: async () =>
      api.organization.organizationControllerGetAllSpendCategories(
        organizationId,
        {
          ...query,
          page: query?.page || 1,
          size: query?.size || MAXIMUM_PAGINATION,
        }
      ),
  });
};

export interface CreateRequestedSpendAreaPayload {
  organizationId: string;
  spendAreaIds: string[];
}

export const useCreateRequestedSpendArea = (
  options?: UseMutationOptions<
    RequestSpendAreaResponseDto,
    Error,
    CreateRequestedSpendAreaPayload
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.SpendArea],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async ({ organizationId, spendAreaIds }) =>
      api.organization.requestSpendAreaControllerCreateManyRequestedSpendArea(
        organizationId,
        { spendAreaIds }
      ),
  });
};
